import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardIcon, SmartphoneIcon, TabletIcon, LaptopIcon } from '../../icons';
import type { Category, Grade } from './mockProducts';
import { CATEGORIES, GRADES, COLOR_HEX } from './mockProducts';
import { PriceRangeSlider } from './PriceRangeSlider';
import styles from './CatalogueFilters.module.css';

const CATEGORY_ICONS: Record<Category, typeof SmartphoneIcon> = {
  all: DashboardIcon,
  smartphones: SmartphoneIcon,
  tablettes: TabletIcon,
  laptops: LaptopIcon,
};

interface CatalogueFiltersProps {
  activeCategory: Category;
  onCategoryChange: (cat: Category) => void;
  activeGrades: Set<Grade>;
  onGradeToggle: (grade: Grade) => void;
  years: number[];
  activeYears: Set<number>;
  onYearToggle: (year: number) => void;
  colors: string[];
  activeColors: Set<string>;
  onColorToggle: (color: string) => void;
  brands: string[];
  activeBrands: Set<string>;
  onBrandToggle: (brand: string) => void;
  categoryCounts: Record<Category, number>;
  prices: number[];
  priceBounds: { min: number; max: number };
  priceRange: [number, number];
  onPriceChange: (value: [number, number]) => void;
}

export function CatalogueFilters({
  activeCategory,
  onCategoryChange,
  activeGrades,
  onGradeToggle,
  years,
  activeYears,
  onYearToggle,
  colors,
  activeColors,
  onColorToggle,
  brands,
  activeBrands,
  onBrandToggle,
  categoryCounts,
  prices,
  priceBounds,
  priceRange,
  onPriceChange,
}: CatalogueFiltersProps) {
  const { t, i18n } = useTranslation('catalogue');
  const lang = i18n.language;
  const [colorsOpen, setColorsOpen] = useState(false);
  const [showAllColors, setShowAllColors] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const VISIBLE_COLORS = 7;
  const VISIBLE_BRANDS = 7;
  const visibleColors = showAllColors ? colors : colors.slice(0, VISIBLE_COLORS);
  const hiddenColorCount = colors.length - VISIBLE_COLORS;
  const visibleBrands = showAllBrands ? brands : brands.slice(0, VISIBLE_BRANDS);
  const hiddenCount = brands.length - VISIBLE_BRANDS;

  return (
    <div className={styles.sidebar}>
      <ul className={styles.categoryList}>
        {CATEGORIES.map(cat => {
          const Icon = CATEGORY_ICONS[cat.key];
          const isActive = activeCategory === cat.key;
          const itemClass = `${styles.categoryItem}${isActive ? ` ${styles.active}` : ''}`;
          return (
            <li key={cat.key} className={itemClass} onClick={() => onCategoryChange(cat.key)}>
              <span className={styles.categoryIcon}>
                <Icon size={18} />
              </span>
              {lang === 'fr' ? cat.labelFr : cat.labelEn}
              <span className={styles.categoryCount}>{categoryCounts[cat.key]}</span>
            </li>
          );
        })}
      </ul>

      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>{t('demo.filters.grade')}</div>
        <div className={styles.gradeChips}>
          {GRADES.map(grade => {
            const isActive = activeGrades.has(grade);
            const chipClass = `${styles.gradeChip}${isActive ? ` ${styles.active}` : ''}`;
            return (
              <button key={grade} className={chipClass} onClick={() => onGradeToggle(grade)}>
                {grade}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>{t('demo.filters.year')}</div>
        <div className={styles.gradeChips}>
          {years.map(year => {
            const isActive = activeYears.has(year);
            const chipClass = `${styles.gradeChip}${isActive ? ` ${styles.active}` : ''}`;
            return (
              <button key={year} className={chipClass} onClick={() => onYearToggle(year)}>
                {year}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.filterSection}>
        <button
          className={styles.filterToggle}
          onClick={() => setColorsOpen(prev => !prev)}
        >
          <span className={styles.filterLabel}>{t('demo.filters.color')}</span>
          <svg
            className={`${styles.chevron}${colorsOpen ? ` ${styles.chevronOpen}` : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 4.5L6 7.5L9 4.5" />
          </svg>
        </button>
        <div className={`${styles.brandList}${colorsOpen ? ` ${styles.brandListOpen}` : ''}`}>
          <div className={styles.brandListInner}>
            {visibleColors.map(color => (
              <label key={color} className={styles.brandItem}>
                <input
                  type="checkbox"
                  checked={activeColors.has(color)}
                  onChange={() => onColorToggle(color)}
                />
                <span
                  className={styles.filterColorDot}
                  style={{ background: COLOR_HEX[color] || '#6b7280' }}
                />
                {color}
              </label>
            ))}
            {hiddenColorCount > 0 && (
              <button
                className={styles.showMore}
                onClick={() => setShowAllColors(prev => !prev)}
              >
                {showAllColors
                  ? t('demo.filters.showLess')
                  : t('demo.filters.showMore', { count: hiddenColorCount })}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>{t('demo.filters.price')}</div>
        <PriceRangeSlider
          prices={prices}
          min={priceBounds.min}
          max={priceBounds.max}
          step={10}
          value={priceRange}
          onChange={onPriceChange}
        />
      </div>

      <div className={styles.filterSection}>
        <button
          className={styles.filterToggle}
          onClick={() => setBrandsOpen(prev => !prev)}
        >
          <span className={styles.filterLabel}>{t('demo.filters.brand')}</span>
          <svg
            className={`${styles.chevron}${brandsOpen ? ` ${styles.chevronOpen}` : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 4.5L6 7.5L9 4.5" />
          </svg>
        </button>
        <div className={`${styles.brandList}${brandsOpen ? ` ${styles.brandListOpen}` : ''}`}>
          <div className={styles.brandListInner}>
            {visibleBrands.map(brand => (
              <label key={brand} className={styles.brandItem}>
                <input
                  type="checkbox"
                  checked={activeBrands.has(brand)}
                  onChange={() => onBrandToggle(brand)}
                />
                {brand}
              </label>
            ))}
            {hiddenCount > 0 && (
              <button
                className={styles.showMore}
                onClick={() => setShowAllBrands(prev => !prev)}
              >
                {showAllBrands
                  ? t('demo.filters.showLess')
                  : t('demo.filters.showMore', { count: hiddenCount })}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
